import {
  ArrowRight,
  CalendarDays,
  Database,
  Info,
  PlaneLanding,
  PlaneTakeoff,
  RotateCcw,
  Search,
  X,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import planinMarkUrl from "../../visualization/public/planin-logo-mark.png";
import { FlowMap } from "./components/FlowMap";
import type { Direction, OdData, TimeSetKey } from "./types";
import { airportFlows, compactTrips, counterpartName, formatTrips, regionByName } from "./utils";

function LoadingState() {
  return (
    <main className="loading-screen" role="status">
      <img src={planinMarkUrl} alt="" />
      <div><span /><span /></div>
      <p>Loading Chennai airport flows…</p>
    </main>
  );
}

export default function App() {
  const [data, setData] = useState<OdData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [direction, setDirection] = useState<Direction>("to");
  const [timeSet, setTimeSet] = useState<TimeSetKey>("allDay");
  const [selected, setSelected] = useState<string[]>([]);
  const [hoveredRegion, setHoveredRegion] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [flowLimit, setFlowLimit] = useState(30);

  useEffect(() => {
    fetch("/data/od-data.json")
      .then((response) => {
        if (!response.ok) throw new Error(`OD data request failed (${response.status})`);
        return response.json() as Promise<OdData>;
      })
      .then(setData)
      .catch((reason: unknown) => setError(reason instanceof Error ? reason.message : "The dataset could not be loaded."));
  }, []);

  const lookup = useMemo(() => regionByName(data?.regions ?? []), [data?.regions]);
  const allAirportFlows = useMemo(() => {
    if (!data) return [];
    return airportFlows(data.flowsByTimeSet[timeSet], data.meta.airportRegion, direction, selected);
  }, [data, direction, selected, timeSet]);
  const totalTrips = allAirportFlows.reduce((sum, flow) => sum + flow.t, 0);
  const largestTrips = Math.max(1, allAirportFlows[0]?.t ?? 0);
  const matchingRegions = useMemo(() => {
    if (!data) return [];
    const normalized = query.trim().toLocaleLowerCase();
    return data.regions
      .filter((region) => !region.isAirport)
      .filter((region) => !normalized || `${region.shortName} ${region.group}`.toLocaleLowerCase().includes(normalized))
      .sort((left, right) => left.shortName.localeCompare(right.shortName));
  }, [data, query]);

  if (error) {
    return <main className="error-screen"><h1>Chennai flows are unavailable</h1><p>{error}</p></main>;
  }
  if (!data) return <LoadingState />;

  const toggleRegion = (name: string) => {
    setSelected((current) => current.includes(name) ? current.filter((item) => item !== name) : [...current, name]);
  };
  const topFlow = allAirportFlows[0];
  const topCounterpart = topFlow ? counterpartName(topFlow, direction) : null;

  return (
    <div className="app-shell">
      <header className="app-header">
        <div className="brand-lockup">
          <img src={planinMarkUrl} alt="" aria-hidden="true" />
          <span>PlaniN</span>
        </div>
        <div className="title-lockup">
          <p>TomTom O/D analysis</p>
          <h1>Chennai airport flows</h1>
        </div>
        <div className="report-context">
          <span><CalendarDays aria-hidden="true" />{data.meta.dateLabel}</span>
          <span><Database aria-hidden="true" />{data.meta.complete ? "Accepted report" : data.meta.analysisStatus}</span>
        </div>
      </header>

      {!data.meta.complete && (
        <div className="pending-banner" role="status">
          <Info aria-hidden="true" />
          <span>
            {data.meta.analysisStatus === "ENDED"
              ? `TomTom counted ${data.meta.tripsCounted.toLocaleString("en-IN")} trips for analysis ${data.meta.analysisId}. The flow matrix is ready for report acceptance.`
              : `TomTom is calculating analysis ${data.meta.analysisId}. Zone geometry is ready; flows appear after report acceptance.`}
          </span>
        </div>
      )}

      <main id="workspace" className="workspace">
        <aside className="control-rail" aria-label="Flow controls">
          <div className="rail-heading">
            <div><p className="eyebrow">Airport movement</p><h2>Set the direction</h2></div>
            {(selected.length > 0 || query) && (
              <button className="icon-button" type="button" aria-label="Reset zone selection" onClick={() => { setSelected([]); setQuery(""); }}>
                <RotateCcw aria-hidden="true" />
              </button>
            )}
          </div>
          <p className="rail-copy">The airport stays fixed. Choose whether the map shows trips arriving at or departing from it.</p>

          <div className="direction-switch" role="group" aria-label="Airport flow direction">
            <button className={direction === "to" ? "is-active" : ""} type="button" aria-pressed={direction === "to"} onClick={() => setDirection("to")}>
              <PlaneLanding aria-hidden="true" /><span>To airport<small>Destination selected</small></span>
            </button>
            <button className={direction === "from" ? "is-active" : ""} type="button" aria-pressed={direction === "from"} onClick={() => setDirection("from")}>
              <PlaneTakeoff aria-hidden="true" /><span>From airport<small>Origin selected</small></span>
            </button>
          </div>

          <section className="airport-card" aria-label="Fixed airport zone">
            <span className="airport-code">MAA</span>
            <div><small>{direction === "to" ? "Destination" : "Origin"}</small><strong>Chennai International Airport</strong></div>
          </section>

          <section className="rail-section">
            <label className="field-label" htmlFor="time-set">Analysis period</label>
            <select id="time-set" value={timeSet} onChange={(event) => setTimeSet(event.target.value as TimeSetKey)}>
              {data.timeSets.map((item) => <option key={item.key} value={item.key}>{item.label} · {item.window}</option>)}
            </select>
          </section>

          <section className="rail-section zone-filter">
            <div className="field-label-row"><label htmlFor="zone-search">Counterpart zones</label><span>{selected.length ? `${selected.length} selected` : "All zones"}</span></div>
            <div className="search-control"><Search aria-hidden="true" /><input id="zone-search" type="search" value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search Chennai zones" />{query && <button type="button" aria-label="Clear search" onClick={() => setQuery("")}><X aria-hidden="true" /></button>}</div>
            <div className="zone-list" role="listbox" aria-label="Chennai zones" aria-multiselectable="true">
              {matchingRegions.map((region) => (
                <button
                  key={region.name}
                  type="button"
                  role="option"
                  aria-selected={selected.includes(region.name)}
                  className={selected.includes(region.name) ? "is-selected" : ""}
                  onClick={() => toggleRegion(region.name)}
                  onMouseEnter={() => setHoveredRegion(region.name)}
                  onMouseLeave={() => setHoveredRegion(null)}
                >
                  <span>{region.shortName}<small>{region.group}</small></span><i aria-hidden="true" />
                </button>
              ))}
            </div>
          </section>

          <section className="rail-section">
            <div className="field-label-row"><label htmlFor="flow-limit">Map flow density</label><output htmlFor="flow-limit">Top {flowLimit}</output></div>
            <input id="flow-limit" type="range" min="10" max="60" step="10" value={flowLimit} onChange={(event) => setFlowLimit(Number(event.target.value))} />
            <div className="range-labels"><span>Clear</span><span>Dense</span></div>
          </section>
        </aside>

        <section className="map-panel" aria-label="Chennai airport flow map">
          <FlowMap
            data={data}
            direction={direction}
            flows={allAirportFlows}
            flowLimit={flowLimit}
            selected={selected}
            hoveredRegion={hoveredRegion}
            onHoverRegion={setHoveredRegion}
            onSelectRegion={(name) => setSelected((current) => current.length === 1 && current[0] === name ? [] : [name])}
          />
        </section>

        <aside className="analysis-dock" aria-label="Airport flow analysis">
          <section className="analysis-hero">
            <p className="eyebrow">Observed airport movement</p>
            <strong>{compactTrips(totalTrips)}</strong>
            <span>TomTom trips / average weekday</span>
            <p>{direction === "to" ? "Arriving at" : "Departing from"} Chennai Airport across {allAirportFlows.length} active zone pairs.</p>
          </section>

          <section className="route-summary">
            <div><span className="role-marker">{direction === "to" ? "O" : "A"}</span><p><small>{direction === "to" ? "Origin" : "Origin airport"}</small><strong>{direction === "to" ? (selected.length === 0 ? "All Chennai zones" : selected.length === 1 ? lookup.get(selected[0])?.shortName : `${selected.length} selected zones`) : "Chennai Airport"}</strong></p></div>
            <ArrowRight aria-hidden="true" />
            <div><span className="role-marker destination">{direction === "to" ? "A" : "D"}</span><p><small>{direction === "to" ? "Destination airport" : "Destination"}</small><strong>{direction === "to" ? "Chennai Airport" : (selected.length === 0 ? "All Chennai zones" : selected.length === 1 ? lookup.get(selected[0])?.shortName : `${selected.length} selected zones`)}</strong></p></div>
          </section>

          <section className="top-signal">
            <p className="eyebrow">Largest connection</p>
            {topFlow && topCounterpart ? (
              <button type="button" onClick={() => setSelected([topCounterpart])} onMouseEnter={() => setHoveredRegion(topCounterpart)} onMouseLeave={() => setHoveredRegion(null)}>
                <span>{lookup.get(topCounterpart)?.shortName ?? topCounterpart}<small>{lookup.get(topCounterpart)?.group ?? "Outside study area"}</small></span>
                <strong>{formatTrips(topFlow.t)}</strong>
              </button>
            ) : <p className="empty-state">No accepted flow rows are available yet.</p>}
          </section>

          <section className="ranking-section">
            <div className="section-heading"><div><p className="eyebrow">Airport catchment</p><h2>{direction === "to" ? "Origins by volume" : "Destinations by volume"}</h2></div><span>{data.timeSets.find((item) => item.key === timeSet)?.label}</span></div>
            <div className="ranking-list">
              {allAirportFlows.slice(0, 15).map((flow, index) => {
                const name = counterpartName(flow, direction);
                return (
                  <button key={`${flow.o}-${flow.d}`} type="button" className={selected.includes(name) ? "is-selected" : ""} onClick={() => setSelected([name])} onMouseEnter={() => setHoveredRegion(name)} onMouseLeave={() => setHoveredRegion(null)}>
                    <span className="rank">{String(index + 1).padStart(2, "0")}</span>
                    <span className="rank-label">{lookup.get(name)?.shortName ?? name}<i style={{ transform: `scaleX(${flow.t / largestTrips})` }} /></span>
                    <strong>{formatTrips(flow.t)}</strong>
                  </button>
                );
              })}
            </div>
          </section>

          <section className="truth-note"><Info aria-hidden="true" /><p><strong>Observed sample, not expanded demand</strong><span>Volumes are TomTom probe trips averaged across 23 July weekdays. Curves indicate zone-to-zone movement, not driven road paths.</span></p></section>
        </aside>
      </main>

      <footer className="institutional-footer">PlanIn Lab - an IIT Incubated Innovations Lab</footer>
    </div>
  );
}
